<template>
  <div class="layout-navbars-breadcrumb">
    <!-- 对齐 EB breadcrumb.vue collapseShow：defaults/columns 显示折叠图标 -->
    <div
      v-if="showCollapse"
      class="layout-navbars-breadcrumb-icon"
      :title="themeConfig.isCollapse ? '展开菜单' : '收起菜单'"
      @click="onCollapseClick"
    >
      <el-icon :size="18">
        <Expand v-if="themeConfig.isCollapse" />
        <Fold v-else />
      </el-icon>
    </div>
    <el-breadcrumb
      v-if="showBreadcrumbTrail"
      class="layout-navbars-breadcrumb-hide"
      separator="/"
    >
      <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="`${index}-${item.path}`">
        <!-- 对齐 EB：非末级可点；末级用 span（当前页） -->
        <span v-if="index === breadcrumbs.length - 1" class="layout-navbars-breadcrumb-current">
          {{ item.title }}
        </span>
        <a v-else href="javascript:;" @click.prevent="router.push(item.path)">{{ item.title }}</a>
      </el-breadcrumb-item>
    </el-breadcrumb>
  </div>
</template>

<script setup lang="ts">
/**
 * 顶栏面包屑 + 侧栏折叠（对齐 EB layout/navBars/breadcrumb/breadcrumb.vue）
 * 移动端：isCollapse 绑定左侧 Drawer 显隐（见 Aside.vue）
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Expand, Fold } from '@element-plus/icons-vue';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import useCrmebMenuStore from '@/store/modules/menu';
import {
  buildMenuBreadcrumbChain,
  dedupeBreadcrumbByPath,
  getCrmebFormPageTitle,
  ensureCrmebPrefix,
} from '@/utils/crmebMenu';

const route = useRoute();
const router = useRouter();
const themeStore = useCrmebThemeConfigStore();
const crmebMenu = useCrmebMenuStore();
const themeConfig = computed(() => themeStore.themeConfig);

/** 对应 EB collapseShow：defaults / columns */
const showCollapse = computed(() =>
  ['defaults', 'columns'].includes(themeStore.themeConfig.layout),
);

/** 经典/横向不显示面包屑文字（对齐 EB isShowcrumb） */
const showBreadcrumbTrail = computed(() => {
  const { layout, isBreadcrumb } = themeStore.themeConfig;
  if (layout === 'transverse' || layout === 'classic') return false;
  return !!isBreadcrumb;
});

function onCollapseClick() {
  // 对应 EB onThemeConfigChange：分栏且无子菜单且已折叠时不切换
  const { layout, isCollapse } = themeStore.themeConfig;
  if (layout === 'columns' && !crmebMenu.childNavList.length && isCollapse) {
    return;
  }
  themeStore.toggleCollapse();
}

const breadcrumbs = computed(() => {
  const lookupPath = String(route.meta?.activeMenu || route.path);
  const menuChain = buildMenuBreadcrumbChain(crmebMenu.menuTree, lookupPath);
  let chain: Array<{ path: string; title: string }> = [];
  if (menuChain.length) {
    const last = menuChain[menuChain.length - 1];
    const pageTitle = getCrmebFormPageTitle(route);
    const routePath = ensureCrmebPrefix(String(route.path));
    if (pageTitle && pageTitle !== last.title && routePath !== last.path) {
      chain = [...menuChain, { path: routePath, title: pageTitle }];
    } else {
      chain = menuChain;
    }
  } else {
    const matched = route.matched.filter((r) => r.meta?.title);
    if (matched.length) {
      chain = matched.map((r) => ({
        path: r.path,
        title: String(r.meta?.title || ''),
      }));
    } else {
      chain = [{ path: route.path, title: getCrmebFormPageTitle(route) }];
    }
  }
  return dedupeBreadcrumbByPath(
    chain.filter(
      (item, index, arr) =>
        index === 0 ||
        item.title !== arr[index - 1].title ||
        item.path !== arr[index - 1].path,
    ),
  );
});
</script>

<style scoped lang="scss">
/* 对齐 EB breadcrumb.vue + variables.scss Breadcrumb 段 */
.layout-navbars-breadcrumb {
  height: inherit;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 15px;
  font-size: 14px;

  .layout-navbars-breadcrumb-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    margin-right: 15px;
    color: var(--prev-bg-topBarColor, #282c34);
    opacity: 0.8;
    &:hover {
      opacity: 1;
    }
  }

  .layout-navbars-breadcrumb-hide {
    line-height: 50px;
    font-size: 14px;
  }

  .layout-navbars-breadcrumb-current {
    color: var(--prev-bg-topBarColor, #282c34);
    font-weight: normal;
    font-size: 14px;
  }

  /* 覆盖 EP 默认 bold / 主色 hover，对齐 EB */
  :deep(.el-breadcrumb) {
    font-size: 14px;
    line-height: 1;
  }
  :deep(.el-breadcrumb__item) {
    display: inline-flex;
    align-items: center;
    float: none;
  }
  :deep(.el-breadcrumb__inner),
  :deep(.el-breadcrumb__inner a),
  :deep(.el-breadcrumb__inner.is-link) {
    font-weight: normal !important;
    font-size: 14px !important;
    color: var(--prev-color-text-black, #000) !important;
    opacity: 0.7;
    text-decoration: none;
  }
  :deep(.el-breadcrumb__inner a:hover),
  :deep(.el-breadcrumb__inner.is-link:hover) {
    color: var(--prev-bg-topBarColor, #282c34) !important;
    opacity: 0.85;
  }
  /* 末级当前页：不透明、常规字重 */
  :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
    color: var(--prev-bg-topBarColor, #282c34) !important;
    opacity: 1;
    font-weight: normal !important;
    cursor: text;
  }
  :deep(.el-breadcrumb__separator) {
    color: var(--prev-border-color-hover, #c0c4cc) !important;
    font-weight: normal !important;
    margin: 0 8px;
  }
}
</style>
